import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Compass, Briefcase, Phone } from "lucide-react";
import { Seo } from "../components/Seo";

/** Fælles 404 for hele domænet. Sættes altid til noindex, så ukendte URL'er ikke
 *  bliver indekseret som tomme sider, og brugeren får en vej videre i stedet for
 *  en blank skærm. Brandet afgøres af, om URL'en ligger under /business. */
const NotFoundPage = () => {
  const { pathname } = useLocation();
  const isBusiness = pathname.toLowerCase().startsWith("/business");

  const links = isBusiness
    ? [
        { to: "/business", label: "Votra Business, forside", icon: Briefcase },
        { to: "/business/viden", label: "Viden om økonomi og drift", icon: Compass },
        { to: "/business/kontakt", label: "Kontakt Votra Business", icon: Phone },
        { to: "/", label: "Votra Danmark, kurser", icon: Home },
      ]
    : [
        { to: "/", label: "Forside og kurser", icon: Home },
        { to: "/kalender", label: "Kursuskalender", icon: Compass },
        { to: "/business", label: "Votra Business, drift og økonomi", icon: Briefcase },
        { to: "/kontakt", label: "Kontakt os", icon: Phone },
      ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 px-6 py-20 text-center"
      data-testid="not-found-page">
      <Seo
        title="Siden findes ikke | Votra"
        description="Den side du leder efter findes ikke. Find vej videre til kurser, Votra Business eller kontakt."
        path={pathname}
        noindex
        brand={isBusiness ? "business" : "education"}
      />
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-amber-400 mb-5">Fejl 404</p>
      <h1 className="font-display text-4xl sm:text-5xl font-medium text-white mb-5 max-w-2xl leading-tight">
        Siden findes ikke
      </h1>
      <p className="text-base text-slate-400 max-w-xl mb-12 leading-relaxed">
        Adressen <span className="text-slate-200 break-all">{pathname}</span> findes ikke på votra.dk.
        Måske er linket gammelt, eller der er en stavefejl. Her er de steder folk oftest skal hen.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 w-full max-w-xl">
        {links.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to}
            className="flex items-center gap-3 px-5 py-4 rounded-xl border border-white/10 bg-white/[0.03] text-left text-slate-200 hover:border-amber-400/60 hover:bg-white/[0.06] transition-colors"
            data-testid={`not-found-link-${to.replace(/\//g, "-") || "home"}`}>
            <Icon className="h-5 w-5 text-amber-400 flex-shrink-0" />
            <span className="text-sm font-medium">{label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default NotFoundPage;
